import * as React from "react";
import { Menu, MenuButton, MenuItem, MenuItems, MenuSeparator } from "@headlessui/react";
import { Check, ChevronRight, Circle } from "lucide-react";
import { cn } from "@/src/helpers";

const DropdownMenuStateObserver = ({ open, onOpenChange }) => {
  const previousOpen = React.useRef(open);

  React.useLayoutEffect(() => {
    if (previousOpen.current !== open) {
      onOpenChange(open);
      previousOpen.current = open;
    }
  }, [onOpenChange, open]);

  return null;
};

// Root dropdown menu component
const DropdownMenu = ({ children, onOpenChange, ...props }) => {
  if (!onOpenChange) {
    return (
      <Menu as="div" className="relative inline-block text-left" {...props}>
        {children}
      </Menu>
    );
  }

  return (
    <Menu as="div" className="relative inline-block text-left" {...props}>
      {(slot) => (
        <>
          {onOpenChange && <DropdownMenuStateObserver open={slot.open} onOpenChange={onOpenChange} />}
          {typeof children === "function" ? children(slot) : children}
        </>
      )}
    </Menu>
  );
};

// Trigger component
const DropdownMenuTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <MenuButton
    ref={ref}
    className={cn("inline-flex items-center justify-center", className)}
    {...props}
  >
    {children}
  </MenuButton>
));
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

// Content component (the dropdown panel)
// Portals to <body> by default (Headless UI). Dark mode works because
// ThemeProvider stamps the theme on <html>, which is an ancestor of <body>.
const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, children, ...props }, ref) => (
  <MenuItems
    ref={ref}
    anchor={{ to: "bottom end", gap: sideOffset, padding: 8 }}
    className={cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-2xl p-1 transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in",
      "bg-[var(--qk-paper-3)]",
      "border border-[var(--qk-rule)]",
      "shadow-[0_8px_32px_color-mix(in_srgb,var(--qk-ink)_10%,transparent),0_2px_8px_color-mix(in_srgb,var(--qk-ink)_5%,transparent)]",
      className,
    )}
    transition
    {...props}
  >
    {children}
  </MenuItems>
));
DropdownMenuContent.displayName = "DropdownMenuContent";

// Individual menu item
const DropdownMenuItem = React.forwardRef(({ className, inset, children, disabled, ...props }, ref) => (
  <MenuItem
    as="div"
    disabled={disabled}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm text-[var(--qk-ink)] outline-none transition-colors data-[focus]:bg-[var(--qk-paper-2)] data-[focus]:text-[var(--qk-ink)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      inset && "pl-8",
      className,
    )}
    ref={ref}
    {...props}
  >
    {children}
  </MenuItem>
));
DropdownMenuItem.displayName = "DropdownMenuItem";

// Checkbox item (custom implementation for Headless UI)
const DropdownMenuCheckboxItem = React.forwardRef(({ className, children, checked, onCheckedChange, ...props }, ref) => (
  <MenuItem
    as="div"
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors data-[focus]:bg-[var(--qk-paper-2)] data-[focus]:text-[var(--qk-ink)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    ref={ref}
    onClick={() => onCheckedChange && onCheckedChange(!checked)}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      {checked && <Check className="h-4 w-4" />}
    </span>
    {children}
  </MenuItem>
));
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

// Radio item (custom implementation for Headless UI)
const DropdownMenuRadioItem = React.forwardRef(({ className, children, value, onSelect, ...props }, ref) => (
  <MenuItem
    as="div"
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors data-[focus]:bg-[var(--qk-paper-2)] data-[focus]:text-[var(--qk-ink)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    ref={ref}
    onClick={() => onSelect && onSelect(value)}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <Circle className="h-2 w-2 fill-current" />
    </span>
    {children}
  </MenuItem>
));
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

// Label component
const DropdownMenuLabel = React.forwardRef(({ className, inset, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

// Separator component
const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <MenuSeparator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-[var(--qk-rule)]", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

// Shortcut component (purely visual, no changes needed)
const DropdownMenuShortcut = ({ className, ...props }) => {
  return <span className={cn("ml-auto text-xs tracking-widest opacity-60", className)} {...props} />;
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

// Group component (wrapper div)
const DropdownMenuGroup = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("", className)} {...props} />
));
DropdownMenuGroup.displayName = "DropdownMenuGroup";

// Portal component (Headless UI handles portaling automatically)
const DropdownMenuPortal = ({ children }) => {
  return <>{children}</>;
};
DropdownMenuPortal.displayName = "DropdownMenuPortal";

// Sub menu components (custom implementation for Headless UI)
const DropdownMenuSub = React.forwardRef(({ children, ...props }, ref) => (
  <Menu as="div" className="relative" ref={ref} {...props}>
    {children}
  </Menu>
));
DropdownMenuSub.displayName = "DropdownMenuSub";

const DropdownMenuSubTrigger = React.forwardRef(({ className, inset, children, ...props }, ref) => (
  <MenuButton
    ref={ref}
    className={cn(
      "flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[focus]:bg-[var(--qk-paper-2)] data-[open]:bg-[var(--qk-paper-2)]",
      inset && "pl-8",
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto h-4 w-4" />
  </MenuButton>
));
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

const DropdownMenuSubContent = React.forwardRef(({ className, ...props }, ref) => (
  <MenuItems
    ref={ref}
    anchor="right start"
    className={cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-2xl p-1 transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in",
      "!bg-[var(--qk-paper-3)]",
      "!border !border-[var(--qk-rule)]",
      "!shadow-[0_8px_32px_color-mix(in_srgb,var(--qk-ink)_10%,transparent),0_2px_8px_color-mix(in_srgb,var(--qk-ink)_5%,transparent)]",
      className,
    )}
    transition
    {...props}
  />
));
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";

// Radio group component (wrapper for radio items)
const DropdownMenuRadioGroup = React.forwardRef(({ className, value, onValueChange, children, ...props }, ref) => (
  <div ref={ref} className={cn("", className)} {...props}>
    {React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type === DropdownMenuRadioItem) {
        return React.cloneElement(child, {
          onSelect: onValueChange,
          'data-state': child.props.value === value ? 'checked' : 'unchecked',
        });
      }
      return child;
    })}
  </div>
));
DropdownMenuRadioGroup.displayName = "DropdownMenuRadioGroup";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
